import { TagType } from '@coworkrz/cards/types';
import { SharedPriceDto } from '@coworkrz/core/dto';
import { Currency, CurrencyLocale } from '@coworkrz/core/types';
import { ProductUnit, SubscriptionPriceUnits } from '@coworkrz/products/types';
import { differenceInCalendarDays } from 'date-fns';
import moment from 'moment';
import { capitalize } from 'radash';

const extractDecimalPart = (number: number, precision = 2): number => {
  const multiplier = Math.pow(10, precision);
  const decimalPart = Math.round((number % 1) * multiplier) / multiplier;
  return decimalPart;
};
export const getDuration = (
  startTime: Date,
  endTime: Date,
  minDecimals = 0.17 //10 min => 0.17h
): number => {
  let duration = (endTime.getTime() - startTime.getTime()) / (1000 * 60 * 60); //in hour

  const decimals = extractDecimalPart(duration);

  duration =
    decimals >= minDecimals ? Math.ceil(duration) : Math.floor(duration);
  return duration;
};

export const convertToNumber = (value: number | string): number => {
  if (typeof value === 'string') return +value;
  return value;
};

export const currency = (
  price: number | string,
  locale: CurrencyLocale = 'fr-CM',
  currency: Currency = 'XAF'
): string => {
  const formatter: Intl.NumberFormat = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
  });
  if (typeof price === 'string') price = Number(price);
  return formatter.format(price);
};

const capitalizeFirstLetter = (value: string) => {
  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
};

export const createFullName = (firstName: string, lastName: string) => {
  const capitalizedFirstName = capitalizeFirstLetter(firstName);
  const capitalizedLastName = capitalizeFirstLetter(lastName);
  return `${capitalizedFirstName} ${capitalizedLastName}`;
};

export const getLocalISOTime = () => {
  const tzoffset = new Date().getTimezoneOffset() * 60000; //offset in milliseconds
  return new Date(Date.now() - tzoffset);
};

export const convertToUTC = (date?: Date | string, timezoneOffset?: string) => {
  if (typeof date === 'string' && timezoneOffset) date += timezoneOffset;
  return moment.utc(date);
};

const priceCompareFn = (a: SharedPriceDto, b: SharedPriceDto): number => {
  // Check if one has an endDate and the other does not
  if (!a.endDate && b.endDate) return 1;
  if (a.endDate && !b.endDate) return -1;

  // Both have endDate or both do not have endDate, sort by startDate
  if (a.startDate > b.startDate) return -1;
  if (a.startDate < b.startDate) return 1;

  // Both have the same startDate, prioritize the one with a tag
  if (a.tag && !b.tag) return -1;
  if (!a.tag && b.tag) return 1;

  return 0;
};
export const sortPricesByMostRelevant = <T extends SharedPriceDto>(
  prices: T[],
  tag?: TagType,
  unit?: ProductUnit
): T[] => {
  const pricesBaseOnTag = !tag
    ? prices
    : prices.filter((price) => !price.tag || price.tag === tag);
  const priceBaseOnUnit = !unit
    ? pricesBaseOnTag
    : pricesBaseOnTag.filter((price) => price.unit === unit);
  const pricesCopy: T[] = JSON.parse(JSON.stringify(priceBaseOnUnit));

  pricesCopy.sort(priceCompareFn);

  return pricesCopy;
};

export const appendUnit = (word: string, unit?: ProductUnit): string => {
  if (!unit || unit === ProductUnit.ITEM) return word;

  return `${word} / ${capitalize(unit)}`;
};

export const productIsSubscription = (price: { unit: ProductUnit }) =>
  SubscriptionPriceUnits.includes(price?.unit);

export const selectPrices = <P = SharedPriceDto>(
  prices: SharedPriceDto[],
  tag?: TagType,
  unit?: ProductUnit
) => {
  const pricesCopy = JSON.parse(JSON.stringify(prices)) as SharedPriceDto[];
  const sortedPrices = sortPricesByMostRelevant(pricesCopy, tag, unit);
  const normalPrice = sortedPrices.find(
    (price) => !price.endDate
  ) as SharedPriceDto;
  const discountedPrice = sortedPrices.find((price) => price.endDate);
  const currentPrice = discountedPrice ?? normalPrice;

  return {
    normalPrice: {
      ...normalPrice,
      isSubscription: productIsSubscription(normalPrice),
    } as P,
    discountedPrice: {
      ...discountedPrice,
      isSubscription: discountedPrice
        ? productIsSubscription(discountedPrice)
        : false,
    } as P,
    currentPrice: {
      ...currentPrice,
      isSubscription: productIsSubscription(currentPrice),
    } as P,
  };
};

export const getSubscriptionDuration = (
  startDate: Date,
  endDate: Date
): {
  factor: number;
  unit: 'month' | 'quarter' | 'year';
  formatted: string;
} => {
  const daysInMonth = 28; // min days in a month
  const daysInQuarter = 88; // min days in a quarter
  const daysInYear = 364; // min days in a year

  const days = differenceInCalendarDays(new Date(endDate), new Date(startDate));

  let factor: number;
  let unit: 'month' | 'quarter' | 'year';

  if (days >= daysInYear) {
    factor = Math.round(days / daysInYear);
    unit = 'year';
  } else if (days >= daysInQuarter) {
    factor = Math.round(days / daysInQuarter);
    unit = 'quarter';
  } else {
    factor = Math.round(days / daysInMonth);
    unit = 'month';
  }

  const formatted = `${factor} ${unit}${factor > 1 ? 's' : ''}`;

  return { factor, unit, formatted };
};
